<script setup>
import { getCurrentInstance, computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { router } from '@inertiajs/vue3';
import MainLayout from '@/layout/main/MainLayout.vue';
import Card from 'primevue/card';
import Button from 'primevue/button';

defineOptions({
    layout: MainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    orderCreated: {
        type: Object,
        default: null
    },
    stripePayment: {
        type: Boolean,
        default: false
    },
    stripeSessionId: {
        type: String,
        default: null
    }
});

const isFromPlaceOrder = computed(() => {
    return props.orderCreated && props.orderCreated.id;
});

// When Stripe webhook hasn't processed yet, poll until order appears
const polling = ref(false);
let pollInterval = null;
let pollTimeout = null;

const stopPolling = () => {
    polling.value = false;
    if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
    }
    if (pollTimeout) {
        clearTimeout(pollTimeout);
        pollTimeout = null;
    }
};

// Watch for orderCreated becoming available (props update after router.reload)
watch(() => props.orderCreated, (newVal) => {
    if (newVal && newVal.id && polling.value) {
        stopPolling();
    }
});

onMounted(() => {
    if (props.stripePayment && !props.orderCreated && props.stripeSessionId) {
        polling.value = true;
        pollInterval = setInterval(() => {
            router.reload({ only: ['orderCreated'] });
        }, 3000);

        // Stop polling after 60 seconds
        pollTimeout = setTimeout(() => stopPolling(), 60000);
    }
});

onUnmounted(() => {
    stopPolling();
});

const goToOrders = () => {
    router.visit(proxy.route('client.orders.index'));
};

const goToOrderDetails = () => {
    if (props.orderCreated && props.orderCreated.id) {
        router.visit(proxy.route('client.orders.show', props.orderCreated.id));
    }
};
</script>

<template>
    <div class="checkout-success-page">
        <div class="flex items-center justify-center min-h-[60vh]">
            <Card class="max-w-2xl w-full">
                <template #content>
                    <div class="text-center py-8">
                        <!-- Success Icon -->
                        <div class="mb-6">
                            <div class="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 text-green-600">
                                <i class="pi pi-check text-5xl"></i>
                            </div>
                        </div>

                        <!-- Success Message -->
                        <h1 class="text-3xl font-bold text-surface-900 mb-4">
                            {{ stripePayment ? $t('Payment received!') : (isFromPlaceOrder ? $t('Order created successfully!') : $t('Order completed successfully!')) }}
                        </h1>

                        <!-- Stripe payment - order already created by webhook -->
                        <template v-if="stripePayment && isFromPlaceOrder">
                            <p class="text-lg text-surface-600 mb-4">
                                {{ $t('Your payment has been confirmed and your order is being processed.') }}
                            </p>
                        </template>

                        <!-- Stripe payment - webhook still processing -->
                        <template v-else-if="stripePayment && !isFromPlaceOrder">
                            <p class="text-lg text-surface-600 mb-4">
                                {{ $t('Your payment has been received. Your order is being created and will appear in your orders shortly.') }}
                            </p>
                            <div v-if="polling" class="flex items-center justify-center gap-2 mb-4">
                                <i class="pi pi-spin pi-spinner text-primary"></i>
                                <span class="text-surface-500 text-sm">{{ $t('Processing your order...') }}</span>
                            </div>
                        </template>

                        <!-- Deferred payment flow -->
                        <template v-else-if="isFromPlaceOrder">
                            <p class="text-lg text-surface-600 mb-4">
                                {{ $t('Your order has been created and is awaiting payment confirmation.') }}
                            </p>
                        </template>

                        <!-- Fallback -->
                        <template v-else>
                            <p class="text-lg text-surface-600 mb-8">
                                {{ $t('Your order has been created and is being processed. You can track the status in your orders page.') }}
                            </p>
                        </template>

                        <!-- Order Details -->
                        <div v-if="isFromPlaceOrder && orderCreated" class="bg-surface-50 rounded-lg p-4 mb-6">
                            <h3 class="font-semibold text-surface-900 mb-3">{{ $t('Order Details') }}</h3>
                            <div class="space-y-2 text-sm">
                                <div class="flex justify-between">
                                    <span class="text-surface-600">{{ $t('Order ID') }}:</span>
                                    <span class="font-medium">#{{ orderCreated.id }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-surface-600">{{ $t('Total Items') }}:</span>
                                    <span class="font-medium">{{ orderCreated.total_items }}</span>
                                </div>
                                <div v-if="orderCreated.deferred_payment" class="flex justify-between">
                                    <span class="text-surface-600">{{ $t('Payment Type') }}:</span>
                                    <span class="font-medium text-orange-600">{{ $t('Deferred Payment') }}</span>
                                </div>
                            </div>
                        </div>
                        <hr>
                        <p class="text-md text-surface-600 my-8">
                            {{ stripePayment
                                ? $t('The production process will begin shortly. You can track the progress in the order details.')
                                : (isFromPlaceOrder
                                    ? $t('Once your payment is confirmed, the production process will begin. You can track the progress in the order details.')
                                    : $t('Once your payment is confirmed, you\'ll be able to submit your publication details in the "My Orders" section.'))
                            }}
                        </p>

                        <!-- Action Buttons -->
                        <div class="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button
                                v-if="isFromPlaceOrder"
                                :label="$t('View Order Details')"
                                icon="pi pi-eye"
                                size="large"
                                @click="goToOrderDetails"
                                class="w-full sm:w-auto"
                            />

                            <Button
                                v-if="!isFromPlaceOrder"
                                :label="$t('View Orders')"
                                icon="pi pi-list"
                                size="large"
                                @click="goToOrders"
                                class="w-full sm:w-auto"
                            />

                            <Button
                                :label="$t('Browse More Sites')"
                                icon="pi pi-search"
                                severity="secondary"
                                outlined
                                size="large"
                                @click="router.visit(proxy.route('client.sites.index'))"
                                class="w-full sm:w-auto"
                            />
                        </div>
                    </div>
                </template>
            </Card>
        </div>
    </div>
</template>

<style scoped>
.checkout-success-page {
    padding: 2rem;
    margin: 0 auto;
}

@media (max-width: 768px) {
    .checkout-success-page {
        padding: 1rem;
    }
}
</style>
